import Image from 'next/image';
import { classes } from 'shared';
import { StrapiDelivered, StrapiDeliveredResponse } from 'types';
import { Button } from '../components/general/button';
import { A } from '../components/general/link';
import { Meta } from '../components/meta/meta';

// images
import { strapi } from 'networking';
import { InferGetStaticPropsType } from 'next';
import furnitureGif from '../assets/home/furniture.gif';
import hero from '../assets/home/home-hero-min.jpg';
import { BlueArch, DeliverStepImg, DonateStepImg, DreamStepImg, JumpingKid, WhiteArch } from '../components/img/svgs';

export async function getStaticProps() {
  const [data] = await strapi.get<StrapiDeliveredResponse>(`/api/delivered`);
  const delivered = data.data.attributes;

  return {
    props: { delivered },
    revalidate: 21600,
  };
}

export default function Home({ delivered }: InferGetStaticPropsType<typeof getStaticProps>) {
  return (
    <main className="relative">
      <Meta />
      {/* Header */}
      <section className="to-fold-home relative w-full overflow-hidden">
        <Image
          src={hero}
          alt="Beds for Kids Child Enjoying Bed"
          className="absolute right-0 top-0 z-0 h-[100vw] w-[200%] max-w-none object-cover object-right-bottom lg:left-0 lg:right-auto lg:h-full lg:w-full lg:object-bottom"
          priority
        />
        <div className="absolute left-0 top-0 z-[1] h-[100vw] w-full bg-gradient-to-b from-transparent from-50% to-white lg:hidden" />
        <div className="absolute inset-0 z-10 grid grid-cols-1 content-center pt-[var(--header-height)] lg:grid-cols-2">
          <div className="px-[5vw] pb-8 lg:px-0 lg:pb-0">
            <div className="mx-auto max-w-2xl rounded-lg bg-blue-500 p-8 pb-12 text-center lg:ml-[7vw] lg:mr-0">
              <h1 className="mx-4 text-center font-operetta text-xl font-bold text-white xxs:text-lg sm:text-2xl lg:text-4xl">
                Providing beds &amp; essential furniture
                <br className="lg:hidden" /> to children &amp; their families in need.
              </h1>
              <HomeDonateButtons />
            </div>
          </div>
          <div className="hidden lg:block"></div>
        </div>
        <HomeHeroBed />
      </section>
      {/* Fold (white 1) */}
      <section className="relative bg-white pt-8 text-blue-500 lg:pt-4">
        <div className="mx-auto mb-16 flex max-w-4xl flex-col gap-4 px-4 text-center">
          <h2 className="relative text-center font-operetta text-4xl lg:text-7xl">
            a furniture bank that's furnishing futures<sup className="-top-3 text-lg lg:-top-7 lg:text-2xl">TM</sup>
          </h2>

          <p className="mt-8 font-avenir text-lg leading-6">
            Beds for Kids is a Charlotte-based furniture bank that delivers beds and essential furniture to children and
            families in need. As housing instability and furniture poverty continues to grow, so does our commitment to
            serving more families.
          </p>
          <p className="mt-4 font-avenir text-lg leading-6">
            With the support of our community, we can provide beds, dressers, dining tables, sofas, and other essential
            furniture that help transform empty housing into a safe, stable, and comfortable home. Together, we are
            delivering so much more than furniture. We are furnishing futures for children and families across the
            Charlotte region.
          </p>
          <div className="mt-4">
            <Button link="/how-to-help" className="px-12 font-avenir !text-base">
              how to help
            </Button>
          </div>
        </div>
        <div className="lg:px-auto mx-auto grid max-w-4xl gap-8 px-4 lg:grid-cols-2">
          <Image src={furnitureGif} alt="furniture gif" className="max-w-full" />
          <div className="flex flex-col justify-center gap-4 text-center lg:text-left">
            <h3 className="font-operetta text-6xl">
              not <span className="font-caliban text-7xl font-normal text-green-500">just</span> beds
            </h3>
            <p className="font-avenir text-lg leading-6">
              We&#39;re not JUST beds for kids, we are a furniture bank. In addition to beds, donate your new or gently
              used furniture items and lamps to help us fully furnish a family&#39;s home.
            </p>
            <div className="text-center lg:text-left">
              <Button link="/who-we-are" className="px-12 font-avenir !text-base">
                about us
              </Button>
            </div>
          </div>
        </div>
        <BlueArch className="mt-16" />
      </section>
      {/* Blue 1 */}
      <Since2011 delivered={delivered} />
      {/* White 2 */}
      <div className="mt-1 text-center">
        <Button link="/donate" className="px-12 font-avenir !text-base">
          donate now
        </Button>
      </div>
      <section className="relative bg-white pt-16 text-blue-500">
        <h2 className="relative mx-auto max-w-2xl px-4 text-center font-operetta text-4xl lg:text-6xl">our process</h2>
        <div className="mx-auto mt-8 grid max-w-5xl gap-x-16 gap-y-4 px-16 lg:grid-cols-3 lg:px-0">
          <div className="text-center">
            <DonateStepImg />
            <p className="font-avenir text-5xl">
              you <span className="font-caliban text-6xl text-green-500">donate</span>
            </p>
          </div>
          <div className="text-center">
            <DeliverStepImg />
            <p className="font-avenir text-5xl">
              we <span className="font-caliban text-6xl text-green-500">deliver</span>
            </p>
          </div>
          <div className="text-center">
            <DreamStepImg />
            <p className="font-avenir text-5xl">
              they <span className="font-caliban text-6xl text-green-500">dream</span>
            </p>
          </div>
        </div>
        <p className="mx-auto mt-8 max-w-3xl px-8 text-center font-avenir text-lg leading-6">
          Beds for Kids relies on generous donations of furniture items from the community throughout the year. Once
          those items are received they are brought back to our warehouse for cleaning and repair. This allows us to
          recycle furniture and creates a sustainable way of providing for the needs of our community. The final step in
          the process is delivery day. The Beds for Kids team along with the support of volunteers delivers these items
          to families in need in the Charlotte region.
        </p>
        <div className="mt-8 text-center">
          <Button link="/who-we-are/">who we are</Button>
        </div>
        <BlueArch className="mt-16" />
      </section>
      {/* Blue 2 (to bottom) */}
      <section className="bg-blue-500 pb-16 text-white">
        <div className="mx-auto grid max-w-5xl lg:grid-cols-3">
          <div className="relative overflow-x-clip lg:overflow-x-visible">
            <JumpingKid className="mx-[25%] -mt-6 lg:absolute lg:-right-24 lg:-top-12 lg:mx-auto lg:mt-0 lg:px-8" />
          </div>
          <div className="flex flex-col px-8 py-4 text-center lg:col-span-2 lg:px-0 lg:pt-16 lg:text-left">
            <h2 className="font-operetta text-5xl">our families</h2>
            <p className="font-avenir text-xl">
              We work with Referring Agencies all over the Charlotte Area to help get matched with families in need.
            </p>
            <div className="mt-4">
              <Button bType="secondary-white" link="/agencies">
                become a referring agency
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export function HomeHeroBed() {
  return (
    <div className="pointer-events-none absolute -bottom-[1px] left-0 right-0">
      <svg id="home-hero-bed" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1600 425">
        <defs>
          <style>{'.home-hero-bed-1{fill:#fff;fill-rule:evenodd;}'}</style>
        </defs>
        <path
          className="home-hero-bed-1"
          d="M1555,1c-24.85,0-45,47.45-45,75.07V420s-331.33-50.72-710-50.72S90,420,90,420v-124.94c0-27.61-20.15-75.06-45-75.06S0,267.45,0,295.06v124.94H0v5H1600V76.07c0-27.61-20.15-75.07-45-75.07Z"
        />
      </svg>
    </div>
  );
}

export function HomeDonateButton({ amount, text }: { amount: number; text: string }) {
  return (
    <A
      prefetch={false}
      href={`/donate?amount=${amount}`}
      className={classes(
        amount === 500 && 'bg-green-500',
        'flex min-h-[80px] flex-col items-center justify-center rounded-lg border-2 border-white bg-opacity-80 p-2 text-center lg:min-h-0',
      )}>
      <p className="text-3xl font-semibold">
        <sup className="text-lg">$</sup>
        {String(amount).length === 4 ? `${String(amount).slice(0, 1)},${String(amount).slice(1, 4)}` : amount}
      </p>
      <p className="text-xs text-gray-300">{text}</p>
    </A>
  );
}

function HomeDonateButtons_ORIGINAL() {
  return (
    // Use the commented code when the 'we've moved' banner is removed
    // <div className="relative z-10 mx-auto mt-[10vh] max-w-4xl text-white xxs:mt-[10vh] xs:mt-[20vh] lg:mt-8">
    <div className="relative z-10 mx-auto mt-[10vh] max-w-4xl text-white xxs:mt-[0vh] xs:mt-8 lg:mt-8">
      <p className="text-center font-avenir text-lg font-medium">choose an amount to donate:</p>
      <div className="mt-3 grid grid-cols-2 gap-4 px-4 lg:grid-cols-5">
        <HomeDonateButton amount={1000} text="completely furnish a home for a family" />
        <HomeDonateButton amount={500} text="ship a semi-truck full of donated furniture" />
        <HomeDonateButton amount={250} text="twin bed set for a child" />
        <HomeDonateButton amount={100} text="crib frame for a baby" />
        <A
          prefetch={false}
          href={`/donate`}
          className="hidden min-h-[80px] items-center justify-center rounded-lg border-2 border-white bg-opacity-80 p-2 lg:flex lg:min-h-0">
          <p className="text-center text-2xl leading-5">
            other
            <br />
            amount
          </p>
        </A>
      </div>
      <section className="m-3">
        <p className="px-4 text-center text-xs text-gray-400">
          Secure Payment &bull; This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" className="text-green-400 hover:underline">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://policies.google.com/terms" className="text-green-400 hover:underline">
            Terms of Service
          </a>{' '}
          apply.
        </p>
      </section>
    </div>
  );
}

function HomeDonateButtons() {
  return (
    // Use the commented code when the 'we've moved' banner is removed
    // <div className="relative z-10 mx-auto mt-[10vh] max-w-4xl text-white xxs:mt-[10vh] xs:mt-[20vh] lg:mt-8">
    <div className="relative z-10 mx-auto mt-[10vh] max-w-md text-white xxs:mt-[0vh] xs:mt-8 lg:mt-8">
      <p className="text-center font-avenir text-lg font-medium">choose an amount to donate:</p>
      <div className="mt-3 grid grid-cols-2 gap-4 px-4 lg:grid-cols-2">
        <HomeDonateButton amount={1000} text="completely furnish a home for a family" />
        <HomeDonateButton amount={500} text="ship a semi-truck full of donated furniture" />
        <HomeDonateButton amount={250} text="twin bed set for a child" />
        <HomeDonateButton amount={100} text="crib frame for a baby" />
        <A
          prefetch={false}
          href={`/donate`}
          className="col-span-2 hidden min-h-[80px] items-center justify-center rounded-lg border-2 border-white bg-opacity-80 p-2 lg:flex lg:min-h-0">
          <p className="text-center text-xl font-light leading-5">other amount</p>
        </A>
      </div>
    </div>
  );
}

export function Since2011({ delivered: { beds, served, total } }: { delivered: StrapiDelivered }) {
  return (
    <section className="relative bg-blue-500 pt-8 text-white lg:pt-0">
      <h2 className="relative mx-auto max-w-2xl px-4 text-center font-operetta text-4xl lg:text-6xl">
        beds &amp; furniture delivered since 2011
      </h2>
      <div className="mx-auto mt-8 grid max-w-5xl px-4 font-avenir lg:grid-cols-3">
        <div className="mb-4 border-b-[3px] border-green-500 pb-4 text-center lg:mb-0 lg:border-0 lg:border-r-[3px] lg:pb-0">
          <p className="text-7xl font-semibold">{beds}</p>
          <p className="text-lg">beds delivered</p>
        </div>
        <div className="mb-4 border-b-[3px] border-green-500 pb-4 text-center lg:mb-0 lg:border-0 lg:border-r-[3px] lg:pb-0">
          <p className="text-7xl font-semibold">{served}</p>
          <p className="text-lg">families served</p>
        </div>
        <div className="text-center">
          <p className="text-7xl font-semibold">{total}</p>
          <p className="text-lg">total furniture units delivered</p>
        </div>
      </div>
      <WhiteArch className="mt-8" />
    </section>
  );
}
